import {
  observable,
  action,
  computed,
  configure,
  runInAction,
} from "mobx";
import { createContext} from "react";
import { IAsset as IAsset } from "../models/asset";
import { toast } from "react-toastify";
import agent from "../api/agent";

configure({ enforceActions: "always" });

class AssetStore {
  @observable assetRegistry = new Map();
  @observable assetSearchRegistry = new Map();
  @observable employeeAssetRegistry = new Map();
  @observable asset: IAsset | null = null;
  @observable employeeRegistry = new Map();
  @observable activeListTab: number = 0;
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable target = "";
  @observable editMode = false;

  @computed get assetsByCode() {
    return Array.from(this.assetSearchRegistry.values()).sort(
      (a, b) => a.code.localeCompare(b.code));
  }
  
  @computed get employeeAssetsByCode() {
    return Array.from(this.employeeAssetRegistry.values()).sort(
      (a, b) => a.code.localeCompare(b.code));
  }

  @computed get employeesByName() {
    return Array.from(this.employeeRegistry.values()).sort(
      (a, b) => a.name.localeCompare(b.name));
  }

  @action loadAssets = async () => {
    this.loadingInitial = true;
    try {
      const assets = await agent.Assets.list();
      runInAction("loading assets", () => {
        assets.forEach((asset) => {
          (asset.purchaseDate) ? asset.purchaseDate = asset.purchaseDate.substr(0,10) : asset.purchaseDate = "";
          (asset.inspectionDate) ? asset.inspectionDate = asset.inspectionDate.substr(0,10) : asset.inspectionDate = "";
          (asset.serviceDate) ? asset.serviceDate = asset.serviceDate.substr(0,10) : asset.serviceDate = "";
          (asset.regoDate) ? asset.regoDate = asset.regoDate.substr(0,10) : asset.regoDate = "";
          (asset.insuranceDate) ? asset.insuranceDate = asset.insuranceDate.substr(0,10) : asset.insuranceDate = "";
          (asset.keep[0].keepDate) ? asset.keep[0].keepDate = asset.keep[0].keepDate.substr(0,10) : asset.keep[0].keepDate = "";
          this.assetRegistry.set(asset.id, asset);
          this.assetSearchRegistry.set(asset.id, asset);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load assets error", () => {
        this.loadingInitial = false;
      });
      toast.error("load assets error");
    }
  };

  @action loadEmployeeAssets = async () => {
    this.loadingInitial = true;
    try {
      const assets = await agent.EmployeeAssets.list();
      runInAction("loading assets", () => {
        assets.forEach((asset) => {
          (asset.purchaseDate) ? asset.purchaseDate = asset.purchaseDate.substr(0,10) : asset.purchaseDate = "";
          (asset.inspectionDate) ? asset.inspectionDate = asset.inspectionDate.substr(0,10) : asset.inspectionDate = "";
          (asset.serviceDate) ? asset.serviceDate = asset.serviceDate.substr(0,10) : asset.serviceDate = "";
          (asset.regoDate) ? asset.regoDate = asset.regoDate.substr(0,10) : asset.regoDate = "";
          (asset.insuranceDate) ? asset.insuranceDate = asset.insuranceDate.substr(0,10) : asset.insuranceDate = "";
          this.employeeAssetRegistry.set(asset.id, asset);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load assets error", () => {
        this.loadingInitial = false;
      });
      toast.error("load assets error");
    }
  }

  @action loadAsset = async (id: string) => {
    let asset = this.getAsset(id);
    if (asset) {
      this.asset = asset;
      return asset;
    } else {
      this.loadingInitial = true;
      try {
        asset = await agent.Assets.details(id);
        runInAction("getting asset", () => {
          (asset.purchaseDate) ? asset.purchaseDate = asset.purchaseDate.substr(0,10) : asset.purchaseDate = "";
          (asset.inspectionDate) ? asset.inspectionDate = asset.inspectionDate.substr(0,10) : asset.inspectionDate = "";
          (asset.serviceDate) ? asset.serviceDate = asset.serviceDate.substr(0,10) : asset.serviceDate = "";
          (asset.regoDate) ? asset.regoDate = asset.regoDate.substr(0,10) : asset.regoDate = "";
          (asset.insuranceDate) ? asset.insuranceDate = asset.insuranceDate.substr(0,10) : asset.insuranceDate = "";
          (asset.keep[0].keepDate) ? asset.keep[0].keepDate = asset.keep[0].keepDate.substr(0,10) : asset.keep[0].keepDate = "";
          this.asset = asset;
          this.loadingInitial = false;
          return asset;
        });
      } catch (error) {
        runInAction("get asset error", () => {
          this.loadingInitial = false;
        });
        toast.error("get asset error");
        console.log(error);
      }
    }
    return asset;
  };

  

  @action clearAsset = () => {
    this.asset = null;
  };

  getAsset = (id: string) => {
    return this.assetRegistry.get(Number(id));
  };

  @action createAsset = async (file: File[], asset: IAsset) => {
    this.submitting = true;
    try {
      // asset.createdAt = new Date().toISOString();
      // asset.updatedAt = new Date().toISOString();
      asset.createdAt = new Date().toJSON();
      asset.updatedAt = new Date().toJSON();

      await agent.Assets.create(file, asset);
      runInAction("create asset", () => {
        // this.assetRegistry.set(asset.id, asset);
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create asset error", () => {
        this.submitting = false;
      });
      toast.error("create asset error");
      console.log(error);
    }
  };

  @action editAsset = async (file: File[], asset: IAsset) => {
    this.submitting = true;
    try {
      await agent.Assets.update(file, asset);
      runInAction("editing asset", () => {
        this.assetRegistry.set(asset.id, asset);
        this.asset = asset;
        this.submitting = false;
      });
    } catch (error) {
      runInAction("edit asset error", () => {
        this.submitting = false;
      });
      toast.error("edit asset error");
      console.log(error);
    }
  };

  @action deleteAsset = async (id: string) => {
    this.submitting = true;
    this.target = id;
    try {
      await agent.Assets.delete(id);
      runInAction("deleting asset", () => {
        this.assetRegistry.delete(id);
        this.submitting = false;
        this.target = "";
      });
    } catch (error) {
      runInAction("delete asset error", () => {
        this.submitting = false;
        this.target = "";
      });
      toast.error("delete asset error");
      console.log(error);
    }
  };

  @action generate = async () => {
    let code = "";
    try {
      var asset = await agent.Assets.generate();
      code = asset.code
      runInAction("getting code", () => {
        return code;
      });
    } catch (error) {
      runInAction("get code error", () => {
      });
      toast.error("get code error");
      console.log(error);
    }
    return code;
  };

  @action loadEmployees = async () => {
    this.loadingInitial = true;
    try {
      const employees = await agent.Employees.list();
      runInAction("loading empolyees", () => {
        employees.forEach((employee) => {
          employee.dob = employee.dob.substr(0, 10);
          employee.startDate = employee.startDate.substr(0, 10);
          this.employeeRegistry.set(employee.email, employee);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load Employee List Error", () => {
        this.loadingInitial = false;
      });
      toast.error("load Employee List Error");
    }
  };

  @action setActiveListTab = (activeIndex: number) => {
    this.activeListTab = activeIndex;
  }
  
  @action searchAsset = async (term: string) => {
    let assets = Array.from(this.assetRegistry.values());
    if (term) {
      const selectedAssets = assets.filter((asset) =>
        asset.assetName.toLowerCase().includes(term.toLowerCase()) || 
        asset.model.toLowerCase().includes(term.toLowerCase())
      );
      this.assetSearchRegistry.clear();
      selectedAssets.forEach((asset) => {
          this.assetSearchRegistry.set(asset.id, asset);
        });
    } else {
      this.assetSearchRegistry.clear();
      assets.forEach((asset) => {
         this.assetSearchRegistry.set(asset.id, asset);
      });
    }
};

}

export default createContext(new AssetStore());
